import type { QueryClient } from '@tanstack/react-query';
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { tableMap } from './tableMap';

export interface CreateSupervisorChannelOpts {
  supabase: SupabaseClient;
  queryClient: QueryClient;
  userId: string;
  /** When true, logs every invalidation to console.debug. Off in production. */
  debug?: boolean;
}

/**
 * Opens one Supabase realtime channel that multiplexes listeners for every
 * table in tableMap, scoped to a single user. On every Postgres change, the
 * matching table's invalidator dispatches React Query cache invalidations.
 *
 * Returns the channel so callers can `supabase.removeChannel(channel)` on
 * cleanup. Pure factory — no module-level side effects.
 */
export function createSupervisorChannel({
  supabase,
  queryClient,
  userId,
  debug = false,
}: CreateSupervisorChannelOpts): RealtimeChannel {
  const channel = supabase.channel(`realtime-supervisor:${userId}`);

  for (const [table, config] of Object.entries(tableMap)) {
    const filter = config.filter(userId);
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter },
      (payload) => {
        if (debug) {
          console.debug('[RealtimeSupervisor]', table, payload.eventType, payload);
        }
        config.invalidate(payload, queryClient);
      },
    );
  }

  // Surface subscription health: a silent CHANNEL_ERROR / TIMED_OUT here means
  // NO table ever goes live (the whole live-by-default contract rides on this one
  // channel actually connecting). Log it so a dead realtime path is visible
  // instead of masquerading as "nothing changed".
  channel.subscribe((status, err) => {
    if (debug) {
      console.debug('[RealtimeSupervisor] channel status:', status, err ?? '');
    }
  });
  return channel;
}
